<script setup lang="ts">
    import { onMounted, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { http } from '@/shared/httpWrapper';
    import { sqlQuery } from '@/shared/helpers/api';
    import { longDate } from '@/formatters';
    import { getClientApplicationDetailsLink, getCorrelatedEventDetailsLink } from '@/shared/helpers/routing';
    import UsageStatsBackLink from './UsageStatsBackLink.vue';

    const route = useRoute();
    const router = useRouter();

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);

    function oneDayAgo(): Date {
        return new Date(Date.now() - 24 * 60 * 60 * 1000);
    }

    const selectedEndpoint = ref<string | null>(null);
    const selectedResource = ref<string | null>(null);
    const selectedScope = ref<string | null>(null);
    const selectedClientApplication = ref<string | null>(null);
    const limit = ref<number>(1000);
    const from = ref<Date>(oneDayAgo());
    const to = ref<Date>(new Date());

    const endpoints = ref<string[]>([]);

    type Resource = { resourceType: string; resourceName: string };
    const resources = ref<Resource[]>([]);
    const scopes = ref<string[]>([]);

    type ClientApplication = { userId: string; clientName: string };
    const clientApplications = ref<ClientApplication[]>([]);

    type UsageEvent = {
        correlationId: string;
        timestamp: string;
        clientName: string;
        userId: string;
        endpoint: string;
        resourceType: string;
        resourceName: string;
        success: boolean;
        durationMilliseconds: number;
        scopeArgument: string;
        objectCount: number;
    };

    const results = ref<UsageEvent[]>([]);
    const loading = ref(false);
    const currentPage = ref(1);
    const perPage = 20;

    function syncQueryParams(): void {
        const query: Record<string, string> = {
            from: from.value.toISOString(),
            to: to.value.toISOString(),
            limit: String(limit.value),
            searched: '1'
        };
        if (selectedEndpoint.value) query.endpoint = selectedEndpoint.value;
        if (selectedResource.value) query.resource = selectedResource.value;
        if (selectedScope.value) query.scope = selectedScope.value;
        if (selectedClientApplication.value) query.client = selectedClientApplication.value;
        router.replace({ query });
    }

    function restoreFromQueryParams(): void {
        const q = route.query;
        if (q.endpoint) selectedEndpoint.value = q.endpoint as string;
        if (q.resource) selectedResource.value = q.resource as string;
        if (q.scope) selectedScope.value = q.scope as string;
        if (q.client) selectedClientApplication.value = q.client as string;
        if (q.from) from.value = new Date(q.from as string);
        if (q.to) to.value = new Date(q.to as string);
        if (q.limit) limit.value = Number(q.limit);
    }

    onMounted(async () => {
        const [endpointRows, scopeRows, clientAppRows, resourceRows] = await Promise.all([
            sqlQuery(http, { query: 'SELECT distinct [endpoint] FROM _clientUsageEvent ORDER BY [endpoint]' }),
            sqlQuery(http, { query: 'SELECT distinct [scopeArgument] FROM _clientUsageEvent ORDER BY [scopeArgument]' }),
            sqlQuery(http, { query: 'SELECT distinct [UserId], [ClientName] FROM _clientUsageEvent ORDER BY [ClientName]' }),
            sqlQuery(http, { query: 'SELECT distinct [resourceType], [resourceName] FROM _clientUsageEvent ORDER BY [resourceType], [resourceName]' })
        ]);
        endpoints.value = endpointRows.data.map(r => r['endpoint'] as string);
        scopes.value = scopeRows.data.map(r => r['scopeArgument'] as string);
        clientApplications.value = clientAppRows.data.map(r => ({ userId: r['UserId'] as string, clientName: r['ClientName'] as string }));
        resources.value = resourceRows.data.map(r => ({ resourceType: r['resourceType'] as string, resourceName: r['resourceName'] as string }));

        if (route.query.searched) {
            restoreFromQueryParams();
            await search();
        }
    });

    function reset(): void {
        selectedEndpoint.value = null;
        selectedResource.value = null;
        selectedScope.value = null;
        selectedClientApplication.value = null;
        limit.value = 1000;
        from.value = oneDayAgo();
        to.value = new Date();
    }

    async function search(): Promise<void> {
        const conditions: string[] = [
            `[Timestamp] >= '${from.value.toISOString()}'`,
            `[Timestamp] <= '${to.value.toISOString()}'`
        ];
        if (selectedEndpoint.value) {
            conditions.push(`[Endpoint] = '${selectedEndpoint.value}'`);
        }
        if (selectedScope.value) {
            conditions.push(`[ScopeArgument] = '${selectedScope.value}'`);
        }
        if (selectedClientApplication.value) {
            conditions.push(`[UserId] = '${selectedClientApplication.value}'`);
        }
        if (selectedResource.value) {
            const [resType, resName] = selectedResource.value.split('/');
            conditions.push(`[ResourceType] = '${resType}' AND [ResourceName] = '${resName}'`);
        }

        /*
            SECURITY NOTE:
            Constructing the SQL query with string interpolation without sanitization must be considered safe,
            as our public API already accepts any input SQL from any authorized user, and has to be secured by other means.
        */

        const query = `
            SELECT TOP ${limit.value}
                [CorrelationId],
                [Timestamp],
                [ClientName],
                [UserId],
                [Endpoint],
                [ResourceType],
                [ResourceName],
                [Success],
                [DurationMilliseconds],
                [ScopeArgument],
                [ObjectCount]
            FROM _clientUsageEvent
            WHERE ${conditions.join(' AND ')}
            ORDER BY [Timestamp] DESC`;

        try {
            loading.value = true;
            currentPage.value = 1;
            const response = await sqlQuery(http, { query });
            results.value = response.data.map(r => ({
                correlationId: r['CorrelationId'] as string,
                timestamp: r['Timestamp'] as string,
                clientName: r['ClientName'] as string,
                userId: r['UserId'] as string,
                endpoint: r['Endpoint'] as string,
                resourceType: r['ResourceType'] as string,
                resourceName: r['ResourceName'] as string,
                success: r['Success'] as boolean,
                durationMilliseconds: r['DurationMilliseconds'] as number,
                scopeArgument: r['ScopeArgument'] as string,
                objectCount: r['ObjectCount'] as number
            }));
        } finally {
            loading.value = false;
        }
        syncQueryParams();
    }
</script>

<template>
    <div>
        <h1 class="title mb-1">
            Usage event search
        </h1>
        <usage-stats-back-link />
        <hr>
        <div class="columns">
            <div class="column">
                <b-field label="Endpoint">
                    <b-select
                        v-model="selectedEndpoint"
                        placeholder="All endpoints"
                        expanded>
                        <option :value="null">
                            All endpoints
                        </option>
                        <option
                            v-for="endpoint in endpoints"
                            :key="endpoint"
                            :value="endpoint">
                            {{ endpoint }}
                        </option>
                    </b-select>
                </b-field>
            </div>
            <div class="column">
                <b-field label="Resource">
                    <b-select
                        v-model="selectedResource"
                        placeholder="All resources"
                        expanded>
                        <option :value="null">
                            All resources
                        </option>
                        <option
                            v-for="resource in resources"
                            :key="`${resource.resourceType}-${resource.resourceName}`"
                            :value="`${resource.resourceType}/${resource.resourceName}`">
                            {{ resource.resourceType }}: {{ resource.resourceName }}
                        </option>
                    </b-select>
                </b-field>
            </div>
            <div class="column">
                <b-field label="Scope">
                    <b-select
                        v-model="selectedScope"
                        placeholder="All scopes"
                        expanded>
                        <option :value="null">
                            All scopes
                        </option>
                        <option
                            v-for="scope in scopes"
                            :key="scope"
                            :value="scope">
                            {{ scope }}
                        </option>
                    </b-select>
                </b-field>
            </div>
            <div class="column">
                <b-field label="Client application">
                    <b-select
                        v-model="selectedClientApplication"
                        placeholder="All clients"
                        expanded>
                        <option :value="null">
                            All clients
                        </option>
                        <option
                            v-for="app in clientApplications"
                            :key="app.userId"
                            :value="app.userId">
                            {{ app.clientName }}
                        </option>
                    </b-select>
                </b-field>
            </div>
        </div>
        <div class="columns is-align-items-flex-end">
            <div class="column is-narrow">
                <b-field label="From (UTC)">
                    <b-datetimepicker
                        v-model="from"
                        icon="calendar-today"
                        :icon-right="from ? 'close-circle' : ''"
                        icon-right-clickable
                        :min-datetime="thirtyDaysAgo"
                        :max-datetime="to"
                        :tz-offset="new Date().getTimezoneOffset()"
                        :datetime-formatter="longDate"
                        :timepicker="{ hourFormat: '24' }"
                        editable
                        @icon-right-click="from = oneDayAgo()" />
                </b-field>
            </div>
            <div class="column is-narrow">
                <b-field label="To (UTC)">
                    <b-datetimepicker
                        v-model="to"
                        icon="calendar-today"
                        :icon-right="to ? 'close-circle' : ''"
                        icon-right-clickable
                        :min-datetime="from"
                        :max-datetime="new Date()"
                        :tz-offset="new Date().getTimezoneOffset()"
                        :datetime-formatter="longDate"
                        :timepicker="{ hourFormat: '24' }"
                        editable
                        @icon-right-click="to = new Date()" />
                </b-field>
            </div>
            <div class="column is-narrow">
                <b-field label="Max events">
                    <b-select v-model="limit">
                        <option :value="1000">
                            1 000
                        </option>
                        <option :value="10000">
                            10 000
                        </option>
                        <option :value="100000">
                            100 000
                        </option>
                    </b-select>
                </b-field>
            </div>
            <div class="column is-flex is-justify-content-flex-end pb-3">
                <div class="buttons">
                    <b-button
                        type="is-primary"
                        @click="search">
                        <b-icon
                            icon="magnify"
                            size="is-small" />
                        <span>Search</span>
                    </b-button>
                    <b-button @click="reset">
                        <b-icon
                            icon="refresh"
                            size="is-small" />
                        <span>Reset</span>
                    </b-button>
                </div>
            </div>
        </div>
        <hr>
        <b-table
            :data="results"
            :loading="loading"
            :narrowed="true"
            :hoverable="true"
            :striped="true"
            :paginated="true"
            :per-page="perPage"
            :current-page="currentPage"
            pagination-position="bottom">
            <b-table-column
                v-slot="tableProps"
                field="timestamp"
                label="Timestamp (UTC)"
                sortable>
                {{ longDate(tableProps.row.timestamp) }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="clientName"
                label="Client"
                :searchable="true"
                sortable>
                <router-link
                    v-if="tableProps.row.userId"
                    :to="getClientApplicationDetailsLink(tableProps.row.userId)">
                    {{ tableProps.row.clientName }}
                </router-link>
                <span v-else>{{ tableProps.row.clientName }}</span>
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="endpoint"
                label="Endpoint"
                :searchable="true"
                sortable>
                {{ tableProps.row.endpoint }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="resourceName"
                label="Resource"
                :searchable="true"
                sortable>
                {{ tableProps.row.resourceType }}: {{ tableProps.row.resourceName }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="success"
                label="Success"
                sortable>
                <b-icon
                    :icon="tableProps.row.success ? 'check' : 'close'"
                    :type="tableProps.row.success ? 'is-success' : 'is-danger'"
                    size="is-small" />
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="durationMilliseconds"
                label="Duration (ms)"
                numeric
                sortable>
                {{ tableProps.row.durationMilliseconds }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="scopeArgument"
                label="Scope"
                sortable>
                {{ tableProps.row.scopeArgument }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="objectCount"
                label="Objects"
                numeric
                sortable>
                {{ tableProps.row.objectCount }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="correlationId"
                label="Correlation ID"
                sortable>
                <router-link :to="getCorrelatedEventDetailsLink(tableProps.row.correlationId)">
                    {{ tableProps.row.correlationId }}
                </router-link>
            </b-table-column>
            <template #footer>
                <div class="has-text-left has-text-weight-normal is-italic">
                    {{ results.length }} record{{ results.length !== 1 ? 's' : '' }}
                </div>
            </template>
        </b-table>
    </div>
</template>
